import { CustomizeCheckoutData } from '@/@types/Checkouts/edit-checkout'
import { Button } from '@/components/shared/Button/Button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Loader2 } from 'lucide-react'
import { useLayoutEffect, useRef, useState } from 'react'
import {
  FieldErrors,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { CheckoutPreviewPane } from './CheckoutPreviewPane'
import { CheckoutTemplateId } from '@/utils/checkoutTemplates'
import { BannersTab } from './tabs/BannersTab'
import { LayoutTab } from './tabs/LayoutTab'
import { PaymentTab } from './tabs/PaymentTab'
import { ProductPhotoTab } from './tabs/ProductPhotoTab'
import { UrgencyTab } from './tabs/UrgencyTab'

type PaymentMethod = {
  type: string
  name?: string
}

type PersonalizeCheckoutScreenProps = {
  checkoutName: string
  checkoutId: string
  checkoutBaseUrl?: string
  template: CheckoutTemplateId
  onTemplateChange: (template: CheckoutTemplateId) => void
  previewReload: number
  widePreview: boolean
  previewOpen: boolean
  onPreviewOpenChange: (open: boolean) => void
  activeTab: string
  onTabChange: (tab: string) => void
  values: CustomizeCheckoutData
  setValue: UseFormSetValue<CustomizeCheckoutData>
  register: UseFormRegister<CustomizeCheckoutData>
  errors: FieldErrors<CustomizeCheckoutData>
  clearErrors: UseFormClearErrors<CustomizeCheckoutData>
  watch: UseFormWatch<CustomizeCheckoutData>
  productPhotoPreviewUrl: string | null
  checkoutPrice?: string
  paymentMethods?: PaymentMethod[]
  availablePaymentMethods?: PaymentMethod[]
  isSaving: boolean
  onSave: () => void
}

export function PersonalizeCheckoutScreen({
  checkoutName,
  checkoutId,
  checkoutBaseUrl,
  template,
  onTemplateChange,
  previewReload,
  widePreview,
  previewOpen,
  onPreviewOpenChange,
  activeTab,
  onTabChange,
  values,
  setValue,
  register,
  errors,
  clearErrors,
  watch,
  productPhotoPreviewUrl,
  checkoutPrice,
  paymentMethods,
  availablePaymentMethods,
  isSaving,
  onSave,
}: PersonalizeCheckoutScreenProps) {
  const { t } = useTranslation(['common', 'checkouts'])
  const previewProps = {
    productPhotoPreviewUrl,
    checkoutName,
    checkoutPrice,
  }
  const tabList: { value: string; label: string; badge?: string }[] = [
    { value: 'layout', label: t('checkouts:layout_tab') },
    { value: 'urgency', label: t('common:urgency') },
    { value: 'banners', label: t('checkouts:banners_tab') },
    { value: 'payment', label: t('common:payments') },
    { value: 'product-photo', label: t('checkouts:product_photo_tab') },
  ]

  const gridRef = useRef<HTMLDivElement>(null)
  const footerRef = useRef<HTMLElement>(null)
  const [gridHeight, setGridHeight] = useState<number>()

  useLayoutEffect(() => {
    const grid = gridRef.current
    if (!widePreview || !grid) {
      setGridHeight(undefined)
      return
    }
    // Banners do onboarding entram/saem depois do mount e empurram a grade;
    // a altura precisa acompanhar o topo real e o rodapé fixo de salvar.
    const measure = () => {
      const footer = footerRef.current?.offsetHeight ?? 0
      const top = grid.getBoundingClientRect().top + window.scrollY
      setGridHeight(Math.max(320, Math.floor(window.innerHeight - top - footer - 16)))
    }
    const shell = grid.closest('main')?.parentElement ?? document.body
    const resizeObserver = new ResizeObserver(measure)
    const observeShell = () => {
      resizeObserver.disconnect()
      Array.from(shell.children).forEach((child) => resizeObserver.observe(child))
      if (footerRef.current) resizeObserver.observe(footerRef.current)
      measure()
    }
    const mutationObserver = new MutationObserver(observeShell)
    mutationObserver.observe(shell, { childList: true })
    observeShell()
    window.addEventListener('resize', measure)
    return () => {
      resizeObserver.disconnect()
      mutationObserver.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [widePreview])

  return (
    <>
      <div
        ref={gridRef}
        style={gridHeight ? { height: gridHeight } : undefined}
        className="grid w-full grid-cols-1 items-start gap-6 pb-28 xl:h-[calc(100svh-12rem)] xl:grid-cols-[minmax(0,1fr)_minmax(280px,0.9fr)] xl:grid-rows-[minmax(0,1fr)] xl:items-stretch xl:gap-8 xl:overflow-hidden xl:pb-0 2xl:grid-cols-[minmax(0,1.1fr)_minmax(300px,0.8fr)]"
      >
        <div className="min-w-0 w-full xl:h-full xl:min-h-0 xl:overflow-y-auto xl:overscroll-contain xl:pb-8 xl:pr-2">
          <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
            <h1 className="min-w-0 break-words text-2xl font-semibold leading-tight text-white">
              {checkoutName}
            </h1>
            {widePreview ? null : (
              <Button
                type="button"
                variant="link"
                size="lg"
                className="shrink-0"
                onClick={() => onPreviewOpenChange(true)}
              >
                {t('checkouts:preview_open')}
              </Button>
            )}
          </div>

          <Tabs className="w-full" defaultValue={activeTab} value={activeTab}>
            <TabsList className="w-full bg-transparent sm:hidden">
              <Select value={activeTab} onValueChange={onTabChange}>
                <SelectTrigger className="w-full">
                  <SelectValue
                    placeholder={
                      tabList.find((tab) => tab.value === activeTab)?.label
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {tabList.map((tab) => (
                    <SelectItem
                      value={tab.value}
                      key={tab.value}
                      className="w-full"
                    >
                      {tab.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </TabsList>
            <TabsList className="hidden h-auto w-full gap-1 overflow-x-auto rounded-full bg-[#18181B] p-1 [-ms-overflow-style:none] [scrollbar-width:none] sm:flex [&::-webkit-scrollbar]:hidden">
              {tabList.map((tab) => (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  onClick={(event) => {
                    onTabChange(tab.value)
                    event.currentTarget.scrollIntoView({ block: 'nearest', inline: 'nearest' })
                  }}
                  data-cy={`tab-${tab.value}`}
                  className="shrink-0 grow rounded-full px-3 py-1.5 text-sm data-[state=active]:bg-[#27272A] data-[state=active]:text-white"
                >
                  <span className="whitespace-nowrap">{tab.label}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            <LayoutTab template={template} onSelect={onTemplateChange} />
            <UrgencyTab
              values={values}
              setValue={setValue}
              register={register}
              errors={errors}
              clearErrors={clearErrors}
              {...previewProps}
            />
            <BannersTab
              values={values}
              setValue={setValue}
              clearErrors={clearErrors}
              errors={errors}
            />
            <PaymentTab
              setValue={setValue}
              watch={watch}
              clearErrors={clearErrors}
              paymentMethods={paymentMethods}
              availablePaymentMethods={availablePaymentMethods}
            />
            <ProductPhotoTab
              values={values}
              setValue={setValue}
              {...previewProps}
            />
          </Tabs>
        </div>
        <CheckoutPreviewPane
          checkoutId={checkoutId}
          checkoutBaseUrl={checkoutBaseUrl}
          template={template}
          reloadToken={previewReload}
          wide={widePreview}
          open={previewOpen}
          onOpenChange={onPreviewOpenChange}
        />
      </div>
      <footer
        ref={footerRef}
        className="fixed bottom-0 left-0 w-full border-t border-t-colorBorder bg-black p-4">
        <div className="flex w-full items-center justify-center gap-6 md:justify-end">
          <Button
            variant="primary"
            className="w-full md:max-w-[148px]"
            type="button"
            data-cy="btn-save-checkout"
            disabled={isSaving}
            onClick={() => {
              if (isSaving) return
              onSave()
            }}
          >
            {isSaving ? (
              <span className="flex items-center gap-2">
                <Loader2 className="animate-spin" />
                {t('saving')}
              </span>
            ) : (
              t('common:save')
            )}
          </Button>
        </div>
      </footer>
    </>
  )
}
